import React from 'react';
import { Icon } from '@iconify/react';

/**
 * PlatformCard - 平台選擇卡片（垂直排列）
 * 用於 creator-to-script 等頁面的平台選擇
 */
export function PlatformCard({
  icon,
  iconColor = '#000',
  name,
  description,
  selected = false,
  onClick,
  className = '',
}) {
  return (
    <button
      onClick={onClick}
      className={`
        flex flex-col items-center justify-center py-8 px-10 rounded-2xl
        cursor-pointer transition-all duration-200 min-w-[180px] border-2
        ${selected
          ? 'bg-slate-50'
          : 'bg-white border-slate-200 hover:bg-slate-50'
        }
        ${className}
      `}
      style={{
        borderColor: selected ? iconColor : undefined
      }}
    >
      <Icon
        icon={icon}
        className="text-5xl mb-3"
        style={{ color: iconColor }}
      />
      <span className="text-base font-semibold text-slate-800 mb-1">
        {name}
      </span>
      {description && (
        <span className="text-xs text-slate-500">
          {description}
        </span>
      )}
    </button>
  );
}

/**
 * OptionCard - 選項卡片組件
 * 用於選擇頁面，如 ai-writing-select, content-generator 等
 */
export function OptionCard({
  icon,
  iconColor = '#3B82F6',
  iconBgColor = '#DBEAFE',
  title,
  subtitle,
  description,
  onClick,
  disabled = false,
  comingSoon = false,
  className = '',
}) {
  const isDisabled = disabled || comingSoon;

  return (
    <div
      onClick={() => !isDisabled && onClick?.()}
      className={`
        relative bg-white rounded-2xl p-6 border border-gray-200
        flex gap-4 items-start
        transition-all duration-200
        ${isDisabled
          ? 'opacity-60 cursor-not-allowed'
          : 'cursor-pointer hover:-translate-y-0.5 hover:shadow-lg hover:border-current'
        }
        ${className}
      `}
      style={{ '--hover-border-color': iconColor }}
    >
      {/* Coming Soon Badge */}
      {comingSoon && (
        <div className="absolute top-3 right-3 bg-amber-500 text-white text-[10px] font-semibold px-2 py-1 rounded-md uppercase tracking-wide">
          Coming Soon
        </div>
      )}

      {/* Icon */}
      <div
        className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
        style={{ background: iconBgColor }}
      >
        <Icon icon={icon} width={24} style={{ color: iconColor }} />
      </div>

      {/* Text */}
      <div className="flex-1 min-w-0">
        {subtitle && (
          <div
            className="text-[11px] font-semibold uppercase tracking-wide mb-1"
            style={{ color: iconColor }}
          >
            {subtitle}
          </div>
        )}
        <h3 className="text-lg font-semibold text-gray-900 mb-1.5">
          {title}
        </h3>
        {description && (
          <p className="text-[13px] text-gray-500 leading-relaxed m-0">
            {description}
          </p>
        )}
      </div>

      {/* Arrow */}
      <Icon
        icon="mdi:chevron-right"
        width={20}
        className="text-gray-400 shrink-0 mt-3.5"
      />
    </div>
  );
}

/**
 * TipBox - 提示區塊組件
 */
export function TipBox({
  icon = 'mdi:information',
  title = 'Tips',
  children,
  className = '',
}) {
  return (
    <div className={`p-5 bg-gray-50 rounded-xl border border-gray-200 ${className}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon icon={icon} width={18} className="text-gray-500" />
        <span className="text-sm font-semibold text-gray-700">{title}</span>
      </div>
      <p className="text-[13px] text-gray-500 m-0 leading-relaxed">
        {children}
      </p>
    </div>
  );
}

/**
 * PageHeader - 頁面標題區組件
 */
export function PageHeader({
  badge,
  badgeIcon,
  title,
  subtitle,
  className = '',
}) {
  return (
    <div className={`text-center mb-12 ${className}`}>
      {badge && (
        <div className="inline-flex items-center gap-2 bg-gray-100 px-4 py-2 rounded-full mb-4">
          {badgeIcon && <Icon icon={badgeIcon} width={16} className="text-gray-500" />}
          <span className="text-[13px] text-gray-500 font-medium">{badge}</span>
        </div>
      )}
      <h1 className="text-4xl font-bold text-gray-900 mb-3">
        {title}
      </h1>
      {subtitle && (
        <p className="text-lg text-gray-500 max-w-[500px] mx-auto">
          {subtitle}
        </p>
      )}
    </div>
  );
}

export default OptionCard;
